import Button from "../../../components/form/button";
import Container from "../../../components/Container";
import { Helmet } from "react-helmet";
import { Input } from "../../../components/ui/input";
import { useEffect, useState } from "react";
import Multiselect from "../../../components/form/multiselect";
import List, { ListObject } from "../../../components/form/List";
import { createSlug } from "../../../lib/utils";
import { Status } from "../../../lib/types";
import ErrorMessage from "../../../components/form/errorMessage";
import pb from "../../../lib/pocketbase";
import { useToast } from "../../../components/ui/use-toast";
import { RecipeTags } from "../../../lib/consts";
import MarkdownEditor from "../../../components/form/MarkdownEditor";
import { useNavigate } from "react-router-dom";

interface RecipeData {
    title: string;
    slug: string;
    servings: string;
    tags: string[];
    ingredients: ListObject[];
    recipe: string;
    user?: string;
    image: File | null;
    duration: string;
}

export default function NewRecipe() {
    const navigate = useNavigate();
    const [data, setData] = useState<RecipeData>({
        title: "",
        slug: "",
        servings: "",
        tags: [],
        ingredients: [],
        recipe: "",
        user: pb.authStore?.model?.id,
        image: null,
        duration: ""
    });
    const [savingStatus, setSavingStatus] = useState<Status>("none");
    const [error, setError] = useState<string>("");
    const { toast } = useToast();

    async function Save() {
        setSavingStatus("loading");
        const fd = new FormData();
        if (!data.user) {
            setSavingStatus("error");
            setError("You are no longer logged in.");
            return;
        }
        if (data.title.length <= 2) {
            setSavingStatus("error");
            setError("Recipe title must be longer than 2 characters");
            return;
        }
        data.slug = createSlug(data.title);
        if (!data.servings) {
            setSavingStatus("error");
            setError("Servings must be declared");
            return;
        }
        if (!data.recipe) {
            setSavingStatus("error");
            setError("Recipe must be declared");
            return;
        }

        const ingredients: string[] = [];

        await Promise.all(
            data.ingredients.map(async (ingredient) => {
                const record = await pb.collection("ingredient").create({
                    ingredient: ingredient.value
                });
                ingredients.push(record.id);
            })
        );

        fd.append("user", data.user);
        fd.append("title", data.title);
        fd.append("slug", data.slug);
        fd.append("recipe", data.recipe);
        fd.append("servings", data.servings);
        fd.append("duration", data.duration);
        data.tags.map((tag) => {
            fd.append("tags", tag);
        });
        ingredients.map((ingredient) => {
            fd.append("ingredients", ingredient);
        });
        if (data.image) fd.append("image", data.image);

        await pb
            .collection("recipe")
            .create(fd)
            .then((res) => {
                toast({
                    title: `👍 Added!`,
                    description: `I have added your new recipe!'`
                });
                setSavingStatus("success");
                navigate("/studio/cookbook/recipe/" + res.id);
            })
            .catch(() => setSavingStatus("error"));
    }

    function onFieldChange(e: React.ChangeEvent<HTMLInputElement>) {
        setData((oldData) => {
            return {
                ...oldData,
                [e.target.id]: e.target.value
            };
        });
    }

    useEffect(() => {
        if (savingStatus === "error") {
            setTimeout(() => {
                setSavingStatus("none");
            }, 2000);
        }
    }, [savingStatus]);

    return (
        <>
            <Helmet>
                <title>ReinVB Studio — New recipe</title>
            </Helmet>
            <Container className="my-4 md:my-8 lg:my-16">
                <div className="flex items-center justify-between">
                    <h2 className="text-3xl font-bold">New recipe.</h2>
                </div>
                <ErrorMessage error={error} close={() => setError("")} className="mt-4" />
                <div className="mt-8 flex flex-col gap-6">
                    <div className="flex flex-col gap-2">
                        <label htmlFor="title" className="font-semibold">
                            Name of the recipe *
                        </label>
                        <Input value={data.title} id="title" type="text" onChange={(e) => onFieldChange(e)} placeholder="Cupcakes" />
                        {data.title && <p className="text-sm">slug: {createSlug(data.title)}</p>}
                    </div>

                    <div className="flex flex-col gap-2">
                        <label htmlFor="servings" className="font-semibold">
                            Servings *
                        </label>
                        <Input value={data.servings} id="servings" type="text" onChange={(e) => onFieldChange(e)} placeholder="4 persons / 12 cupcakes..." />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label htmlFor="duration" className="font-semibold">
                            Duration
                        </label>
                        <Input value={data.duration} id="duration" type="text" onChange={(e) => onFieldChange(e)} placeholder="12 minutes..." />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="font-semibold">Tags</label>
                        <Multiselect options={RecipeTags} selected={data.tags} placeholder="Select your tags" onChange={(selected) => setData({ ...data, tags: selected })} />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="font-semibold">Ingredients</label>
                        <List placeholder="Add new ingredient" list={data.ingredients} onChange={(list) => setData({ ...data, ingredients: list })} />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label htmlFor="image" className="font-semibold">
                            Image
                        </label>
                        <Input
                            className="file:text-slate-400 dark:file:text-slate-500"
                            id="small_size"
                            type="file"
                            onChange={(e) => {
                                const file = e.target.files?.[0]; // Access the first file from the FileList object
                                if (file) {
                                    setData({ ...data, image: file }); // Assign the file to the "avatar" field in the form data using setValue
                                }
                            }}></Input>
                    </div>
                    <div className="flex flex-col gap-2">
                        <label htmlFor="recipe" className="font-semibold">
                            Recipe
                        </label>
                        <MarkdownEditor value={data.recipe} className="h-56" id="recipe" placeholder="Describe the recipe..." onChange={(value) => setData({ ...data, recipe: value })} />
                    </div>
                    <Button status={savingStatus} variant={"dark"} className="w-fit" onClick={Save}>
                        {savingStatus === "loading" && "Adding"}
                        {savingStatus === "success" && "Added"}
                        {savingStatus === "error" && "Error"}
                        {savingStatus === "none" && "Add"}
                    </Button>
                </div>
            </Container>
        </>
    );
}
